<template>
  <el-tooltip
    placement="bottom-start"
    effect="light"
    popper-class="custom-select-tooltip"
    :disabled="!hasTooltipContent"
    enterable
  >
    <template #content>
      <div class="tooltip-names" aria-label="selected-names">
        <span
          v-for="(name, i) in tooltipItems"
          :key="i"
          class="tooltip-name"
          :title="name"
        >
          {{ name }}
        </span>
      </div>
    </template>

    <div class="form-group" @keydown.enter.prevent.self>
      <label v-if="label" :for="name">
        {{ label }}
        <span class="text-danger" v-if="required"> *</span>
      </label>

      <el-select
        ref="elSelect"
        :id="name"
        :name="name"
        v-model="internalValue"
        :multiple="multiple"
        :disabled="!!disabled"
        :placeholder="placeholder"
        :clearable="multiple || clearable"
        :filterable="filterable"
        :remote="remote"
        :remote-method="onRemoteSearch"
        :reserve-keyword="true"
        :loading="isLoading"
        :collapse-tags="multiple"
        collapse-tags-tooltip
        :popper-class="'element-select-popper-' + _uid"
        @change="onElChange"
        @clear="onClear"
        :value-key="valueKey"
        @visible-change="onVisibleChange"
        :class="[
          'custom-el-select',
          { 'has-error': errors && errors[errorKey] },
        ]"
      >
        <el-option
          v-for="opt in _options"
          :key="optionKey(opt)"
          :label="getLabel(opt)"
          :value="opt"
        />
        <template #empty>
          <div class="my-empty-message">
            {{
              isLoading
                ? Lang.get("messages.loading")
                : Lang.get("messages.no_options")
            }}
          </div>
        </template>
        <div class="my-empty-message" v-if="!_options.length">
          {{ Lang.get("messages.no_options") }}
        </div>
      </el-select>

      <small
        class="form-text text-danger"
        v-if="errors && errors[errorKey]"
        v-text="errors[errorKey][0]"
      />
    </div>
  </el-tooltip>
</template>

<script>
import get from "lodash/get";
import set from "lodash/set";
import isEqual from "lodash/isEqual";
import debounce from "lodash/debounce";

export default {
  name: "ElementSelect",

  props: {
    label: String,
    name: { type: String, required: true },
    error_name: String,
    model: { type: [Object, Array], required: true },
    errors: { type: [Object, Array], default: () => ({}) },
    options: { type: Array, required: true, default: () => [] },
    labelField: { type: String, required: true },
    valueField: String,
    multiple: { type: Boolean, default: false },
    disabled: [Boolean, Number],
    required: Boolean,
    reduce: { type: Function, default: (value) => value },
    selectAllOption: { type: Boolean, default: true },
    clearable: { type: Boolean, default: false },
    valueKey: { type: String, default: "id" },
    autoDetectModelType: { type: Boolean, default: true },
    remote: Boolean,
    searchDebounce: {
      type: Number,
      default: 500,
    },
    minSearchLength: {
      type: Number,
      default: 2,
    },
    filterable: { type: Boolean, default: true },
    searchDebounce: { type: Number, default: 300 },
  },

  data() {
    return {
      modelStoresObjects: false,
      modelStoresObjectArray: false,
      isLoading: false,
    };
  },

  computed: {
    placeholder() {
      return `${this.Lang.get("messages.select")} ${
        this.label || this.Lang.get("messages.option")
      }`;
    },

    errorKey() {
      return this.error_name || this.name;
    },

    _options() {
      const opts = [...this.options];
      if (this.multiple && this.selectAllOption && !this.haveAllOption()) {
        opts.unshift({
          id: -1,
          [this.labelField]: this.Lang.get("messages.select_all"),
        });
      }
      return opts;
    },

    internalValue: {
      get() {
        const modelVal = get(this.model, this.name);

        if (this.multiple) {
          return this.getMultipleValue(modelVal);
        } else {
          return this.getSingleValue(modelVal);
        }
      },

      set(selected) {
        if (this.multiple) {
          this.setMultipleValue(selected);
        } else {
          this.setSingleValue(selected);
        }
      },
    },

    hasTooltipContent() {
      return this.tooltipItems && this.tooltipItems.length > 1;
    },

    tooltipItems() {
      const sel = this.internalValue || [];

      if (this.multiple) {
        return (Array.isArray(sel) ? sel : [])
          .map((s) => this.getLabel(s))
          .filter(Boolean);
      } else {
        const label = this.getLabel(sel);
        return label ? [label] : [];
      }
    },
  },

  watch: {
    options: {
      handler() {
        this.detectModelStorageType();
        this.syncModelWithOptions();
      },
      immediate: false,
    },

    model: {
      handler() {
        this.detectModelStorageType();
      },
      deep: true,
    },
  },

  created() {
    this.debouncedSearch = debounce((query) => {
      if (query && query.length >= this.minSearchLength) {
        this.$emit("search", query, this.setLoading);
      }
    }, this.searchDebounce);
  },

  methods: {
    // ============ VALUE GETTERS ============
    getMultipleValue(modelVal) {
      if (!Array.isArray(modelVal)) return [];

      return modelVal
        .map((val) => this.findMatchingOption(val))
        .filter(Boolean);
    },

    getSingleValue(modelVal) {
      if (modelVal === null || modelVal === undefined) return null;
      return this.findMatchingOption(modelVal) || null;
    },

    findMatchingOption(val) {
      if (val && typeof val === "object") {
        const keyVal =
          val[this.valueKey] !== undefined ? val[this.valueKey] : val;
        return (
          this._options.find((opt) => isEqual(opt, val)) ||
          this._options.find((opt) => opt[this.valueKey] === keyVal) ||
          this._options.find((opt) => isEqual(this.reduce(opt), keyVal))
        );
      } else {
        return (
          this._options.find((opt) => isEqual(this.reduce(opt), val)) ||
          this._options.find((opt) => opt[this.valueKey] === val)
        );
      }
    },

    // ============ VALUE SETTERS ============
    setMultipleValue(selected) {
      const selectedArray = Array.isArray(selected) ? selected : [];
      const hasAll = selectedArray.some((item) => item?.id === -1);

      let finalSelection = selectedArray.filter((opt) => opt?.id !== -1);

      if (hasAll) {
        finalSelection = this.toggleSelectAll(finalSelection);
      }

      const valueToWrite =
        this.autoDetectModelType && this.modelStoresObjectArray
          ? finalSelection
          : finalSelection.map((opt) => this.reduce(opt));

      this.updateModel(valueToWrite);
      this.$emit("change", valueToWrite);
    },

    setSingleValue(selected) {
      const valueToWrite = selected
        ? this.autoDetectModelType && this.modelStoresObjects
          ? selected
          : this.reduce(selected)
        : null;

      this.updateModel(valueToWrite);
      this.$emit("change", valueToWrite);
    },

    toggleSelectAll(currentSelection) {
      const realOptions = this._options.filter((o) => o.id !== -1);
      const currentModel = get(this.model, this.name) || [];

      const currentOptionObjects = Array.isArray(currentModel)
        ? currentModel
            .map((val) => this.findMatchingOption(val))
            .filter(Boolean)
        : [];

      const allSelected =
        realOptions.length > 0 &&
        realOptions.every((opt) =>
          currentOptionObjects.some(
            (sel) => sel?.[this.valueKey] === opt[this.valueKey]
          )
        );

      return allSelected ? [] : realOptions;
    },

    // ============ MODEL MANAGEMENT ============
    updateModel(value) {
      if (this.name.includes(".")) {
        set(this.model, this.name, value);
      } else {
        this.$set(this.model, this.name, value);
      }
    },

    clearModel() {
      const emptyValue = this.multiple ? [] : null;
      this.updateModel(emptyValue);
      this.$refs.elSelect?.clear?.();
    },

    syncModelWithOptions() {
      const validIDs = new Set(this.options.map((item) => item?.id));
      const currentModel = get(this.model, this.name);

      if (this.multiple && Array.isArray(currentModel)) {
        const newModel = currentModel.filter((el) => {
          if (el && typeof el === "object") {
            return validIDs.has(el.id);
          }
          return this.options.some((opt) => isEqual(this.reduce(opt), el));
        });

        if (newModel.length !== currentModel.length) {
          this.updateModel(newModel);
        }
      } else if (!this.multiple && currentModel !== null) {
        const id =
          currentModel && typeof currentModel === "object"
            ? currentModel.id
            : currentModel;

        if (!validIDs.has(id)) {
          this.clearModel();
        }
      }
    },

    detectModelStorageType() {
      if (!this.autoDetectModelType) return;

      const current = get(this.model, this.name);

      if (this.multiple) {
        this.modelStoresObjectArray =
          Array.isArray(current) &&
          current.length > 0 &&
          current.every((i) => i && typeof i === "object");
      } else {
        this.modelStoresObjects =
          current && typeof current === "object" && !Array.isArray(current);
      }
    },

    // ============ SEARCH FUNCTIONALITY ============
    setLoading(state) {
      this.isLoading = state;
    },

    onRemoteSearch(query) {
      if (!this.remote) return;
      this.debouncedSearch(query);
    },

    // ============ HELPERS ============
    optionKey(opt) {
      return opt?.[this.valueKey] !== undefined
        ? opt[this.valueKey]
        : JSON.stringify(opt);
    },

    haveAllOption() {
      return this.options.some((item) => item?.id === -1);
    },

    getLabel(option) {
      if (!option) return "";
      return typeof option === "object" ? option[this.labelField] : option;
    },

    // ============ EVENT HANDLERS ============
    onElChange() {
      this.$emit("keyup");

      if (this.remote && this.multiple) {
        this.$nextTick(() => {
          this.$refs.elSelect?.$refs?.input?.focus?.();
        });
      }
    },

    onClear() {
      this.clearModel();
      this.$emit("change", this.multiple ? [] : null);
      this.$emit("keyup");

      // Reset remote search results
      if (this.remote) {
        this.$emit("search", "", this.setLoading);
      }
    },

    onVisibleChange(visible) {
      this.$emit("visible-change", visible);

      // Load initial data when dropdown opens for remote search
      if (visible && this.remote && this.options.length === 0) {
        this.$emit("search", "", this.setLoading);
      }
    },
  },

  mounted() {
    this.detectModelStorageType();

    // Load initial data for remote search if needed
    if (this.remote && this.options.length === 0) {
      this.$emit("search", "", this.setLoading);
    }
  },

  beforeDestroy() {
    // Cancel any pending debounced search
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  },
};
</script>

<style scoped>
.custom-el-select {
  width: 100%;
  max-width: 100%;
  display: inline-block;
}

.custom-el-select >>> .el-input__inner {
  box-sizing: border-box;
  padding: 10px;
  padding-inline-end: 37px;
  height: 40px;
  border: 1px solid #dcdfe6 !important;
  box-shadow: none !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.custom-el-select >>> .el-select-dropdown {
  min-width: 200px;
}

.custom-el-select >>> .el-select__tags {
  padding-left: 6px;
  padding-right: 6px;
}

.custom-el-select >>> .el-tag {
  background-color: #e0e0e0;
  color: #333;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 13px;
}

.custom-el-select.has-error >>> .el-input__inner {
  border-color: #f56c6c !important;
  box-shadow: 0 0 0 4px rgba(245, 108, 108, 0.08) !important;
}

.el-tag .el-icon-close {
  right: 0px !important;
}

.el-select__tags-text {
  padding: 0.2rem !important;
}

.my-empty-message {
  padding: 10px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}
</style>

<style>
.custom-select-tooltip {
  width: 40vw !important;
  max-width: 600px;
  min-width: 250px;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  white-space: normal;
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.3;
  border-radius: 6px;
  box-sizing: border-box;
}

.custom-select-tooltip .tooltip-names {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.custom-select-tooltip .tooltip-name {
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  color: #333;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 100%;
}

.is-focus {
  border-color: #409eff !important;
}

html[lang="ar"] .custom-select-tooltip .tooltip-names {
  direction: rtl;
  text-align: right;
}

html[lang="en"] .custom-select-tooltip .tooltip-names {
  direction: ltr;
  text-align: left;
}
</style>
